import { ExclamationCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { Alert, Empty, Space, Spin, Typography } from 'antd';
import React, { useMemo } from 'react';
import styles from './Preprocessed.module.scss';

const { Title } = Typography;

interface PreprocessedDataItem {
  Link: string;
  DisplayName: string;
  AnalyticType: string;
}

interface PreprocessedReportsContentProps {
  dataSW?: PreprocessedDataItem[];
  dataHD?: PreprocessedDataItem[];
  dataWind?: PreprocessedDataItem[];

  isLoadingSW: boolean;
  isLoadingHD: boolean;
  isLoadingWind: boolean;

  errorSW?: unknown;
  errorHD?: unknown;
  errorWind?: unknown;
}

export const ReportsPreprocessedContent: React.FC<
  PreprocessedReportsContentProps
> = ({
  dataSW,
  dataHD,
  dataWind,
  isLoadingSW,
  isLoadingHD,
  isLoadingWind,
  errorSW,
  errorHD,
  errorWind,
}) => {
  const groupedData = useMemo(() => {
    const allData = [...(dataSW || []), ...(dataHD || []), ...(dataWind || [])];

    const groups = {
      extremeValues: [] as PreprocessedDataItem[],
      nss: [] as PreprocessedDataItem[],
    };

    allData.forEach((item) => {
      if (item.AnalyticType === 'XLS') {
        groups.extremeValues.push(item);
      } else if (item.AnalyticType === 'NSS') {
        groups.nss.push(item);
      }
    });

    return groups;
  }, [dataSW, dataHD, dataWind]);

  const renderSection = (title: string, items: PreprocessedDataItem[]) => {
    if (items.length === 0) return null;

    return (
      <div style={{ marginBottom: '24px' }}>
        <Title
          level={5}
          style={{ marginBottom: '8px', fontSize: '14px', fontWeight: 600 }}
        >
          {title}
        </Title>
        <Space direction='vertical' size={4} style={{ width: '100%' }}>
          {items.map((item, index) => (
            <a
              key={`${item.DisplayName}-${index}`}
              href={item.Link}
              target='_blank'
              rel='noopener noreferrer'
              style={{
                fontSize: '13px',
                color: '#1890ff',
                display: 'inline-block',
                width: 'fit-content',
                lineHeight: '1.5',
              }}
            >
              {item.DisplayName}
            </a>
          ))}
        </Space>
      </div>
    );
  };

  const renderDatasetStatus = (
    label: string,
    isLoading: boolean,
    error: unknown
  ) => {
    if (isLoading) {
      return (
        <div
          style={{
            fontSize: '12px',
            color: '#999',
            marginBottom: '4px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <Spin indicator={<LoadingOutlined style={{ fontSize: 12 }} spin />} />
          <span>Loading {label} data...</span>
        </div>
      );
    }
    if (error) {
      return (
        <div style={{ marginBottom: '4px' }}>
          <Alert
            message={`Failed to load ${label} data`}
            type='error'
            showIcon
            icon={<ExclamationCircleOutlined />}
            style={{ padding: '4px 8px', fontSize: '11px' }}
          />
        </div>
      );
    }
    return null;
  };

  const hasAnyData =
    groupedData.extremeValues.length > 0 || groupedData.nss.length > 0;
  const isAllError = errorSW && errorHD && errorWind;

  return (
    <div style={{ padding: '16px' }}>
      <div style={{ marginBottom: '16px' }}>
        {renderDatasetStatus('Waves', isLoadingSW, errorSW)}
        {renderDatasetStatus('Ocean', isLoadingHD, errorHD)}
        {renderDatasetStatus('Atmosphere', isLoadingWind, errorWind)}
      </div>

      {renderSection('Extreme Values', groupedData.extremeValues)}
      {renderSection('Normal Sea states (NSS)', groupedData.nss)}

      {!hasAnyData && !isLoadingSW && !isLoadingHD && !isLoadingWind && (
        <div className={styles.preprocessedEmptyContainer}>
          <Empty
            description={
              isAllError
                ? 'Unable to load reports'
                : 'No pre-processed reports available'
            }
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </div>
      )}
    </div>
  );
};
